import { Attention, WorkItem } from '../api/workItems';

export type AppliedSourceRule = {
    source_key: string;
    attention: Attention;
    applied_ids?: string[];
};

const PROTECTED_REASONS = new Set([
    'quarantine_suspicious',
    'protected_manual_sync',
    'protected_application',
]);

const UNTEACHABLE_SOURCE_KEYS = new Set(['unscoped', 'other']);

export function isTeachableSourceKey(sourceKey: string | null | undefined): boolean {
    const key = (sourceKey || '').trim().toLowerCase();
    return Boolean(key) && !UNTEACHABLE_SOURCE_KEYS.has(key);
}

export function isSourceTeachEligible(item: WorkItem | null | undefined): boolean {
    if (!item) return false;
    if (!isTeachableSourceKey(item.source_key)) return false;
    if ((item.type || '').toUpperCase() === 'APPLICATION') return false;
    if (item.attention_reason === 'quarantine_suspicious') return false;
    if (item.suspicious_classification) return false;
    const state = (item.state || '').toUpperCase();
    return (
        state === 'TRIAGE' ||
        item.attention === 'watch' ||
        item.attention_source === 'rule' ||
        (state === 'ARCHIVED' && item.attention === 'ignore')
    );
}

export function sourceTeachHeadline(item: WorkItem, label: string): string {
    const isStream = item.attention === 'watch' || item.attention_reason === 'watch_stream';
    return isStream ? `Notification stream from ${label}` : `Emails from ${label}`;
}

export function currentSourceRuleAttention(item: WorkItem): Attention | null {
    if (item.attention_source === 'rule' && (item.attention === 'action' || item.attention === 'watch' || item.attention === 'ignore')) {
        return item.attention;
    }
    return null;
}

export function sourceRuleSkipsSibling(item: WorkItem): boolean {
    if ((item.type || '').toUpperCase() === 'APPLICATION') return true;
    const reason = item.attention_reason || '';
    if (reason.startsWith('breakout_')) return true;
    if (PROTECTED_REASONS.has(reason)) return true;
    if (item.suspicious_classification) return true;
    return false;
}

export function itemMatchesStateFilter(item: WorkItem, stateFilter: string): boolean {
    const allowed = new Set(
        stateFilter
            .split(',')
            .map((value) => value.trim().toUpperCase())
            .filter(Boolean),
    );
    if (allowed.size === 0 || allowed.has('ALL')) return true;
    return allowed.has((item.state || '').toUpperCase());
}

function stampSibling(item: WorkItem, attention: Attention): WorkItem {
    if (attention === 'watch') {
        return {
            ...item,
            attention: 'watch',
            attention_source: 'rule',
            attention_reason: 'user_rule',
            needs_action: false,
        };
    }
    if (attention === 'ignore') {
        return {
            ...item,
            attention: 'ignore',
            attention_source: 'rule',
            attention_reason: 'user_rule',
            needs_action: false,
            state: 'ARCHIVED',
        };
    }
    if (attention === 'action') {
        return {
            ...item,
            attention: 'action',
            attention_source: 'rule',
            attention_reason: 'user_rule',
            needs_action: item.attention === 'watch' ? false : item.needs_action,
        };
    }
    return item;
}

export function patchWorkItemsForAppliedSourceRule(
    items: WorkItem[],
    applied: WorkItem,
    rule?: AppliedSourceRule | null,
): WorkItem[] {
    const sourceKey = (rule?.source_key || '').trim();
    const attention = rule?.attention;
    if (!sourceKey || !attention) {
        return items.map((item) => (item.id === applied.id ? applied : item));
    }
    const appliedIds = Array.isArray(rule?.applied_ids) ? new Set(rule.applied_ids) : null;
    return items.map((item) => {
        if (item.id === applied.id) return applied;
        if (appliedIds) {
            if (!appliedIds.has(item.id)) return item;
            return stampSibling(item, attention);
        }
        if (item.source_key !== sourceKey) return item;
        if (sourceRuleSkipsSibling(item)) return item;
        if ((item.state || '').toUpperCase() !== 'TRIAGE') return item;
        return stampSibling(item, attention);
    });
}
